import MultipleChoiceOption from 'models/MultipleChoiceOption';
import Question from 'models/Question';
import React, { useMemo } from 'react';
import ActionButton from 'view/components/buttons/ActionButton';
import { UserInput } from 'view/components/common/UserInput';
import {
  faPencil,
  faPlusSquare,
  faTrash,
} from '@fortawesome/free-solid-svg-icons';
import MultipleChoiceOptionEditor from './MultipleChoiceOptionEditor';

type AssignmentQuestionProps = {
  question: Question;
  onQuestionTextChange: (question: Question, text: string) => void;
  onModifyQuestionChoice?: (
    question: Question,
    choice: MultipleChoiceOption,
    text: string,
    correct: boolean,
  ) => void;
  onDeleteQuestionChoice?: (
    question: Question,
    choice: MultipleChoiceOption,
  ) => void;
  onAddQuestionChoice: (question: Question) => void;
  onUpdateQuestion: (question: Question, submit?: boolean) => void;
  onDeleteQuestion?: (question: Question) => void;
};

const AssignmentQuestion: React.FC<AssignmentQuestionProps> = ({
  question,
  onQuestionTextChange,
  onModifyQuestionChoice,
  onDeleteQuestionChoice,
  onAddQuestionChoice,
  onUpdateQuestion,
  onDeleteQuestion,
}) => {
  const handleReorderQuestionChoices = (
    reorderedChoices: MultipleChoiceOption[],
  ) => {
    const updatedQuestion = Question.fromQuestion(question);
    updatedQuestion.choices = reorderedChoices;
    onUpdateQuestion(updatedQuestion, true);
  };

  const editorQuestion = useMemo(
    () => Question.fromQuestion(question),
    [question],
  );

  return (
    <>
      <UserInput
        key={`question-${editorQuestion.index}-text`}
        id={`question-${editorQuestion.index}-text`}
        label={`Question ${editorQuestion.index}`}
        type="textarea"
        value={editorQuestion.text}
        onChange={(event) =>
          onQuestionTextChange(editorQuestion, event.target.value)
        }
        placeHolder={
          editorQuestion.question_type === 'listening'
            ? 'Type what the user will hear...'
            : 'Type your question...'
        }
        maxLength={800}
      />
      {editorQuestion.question_type === 'listening' && (
        <UserInput
          key={`question-${editorQuestion.index}-prompt`}
          id={`question-${editorQuestion.index}-prompt`}
          label="Prompt"
          type="textarea"
          value={editorQuestion.prompt}
          onChange={(event) =>
            onUpdateQuestion(
              Question.fromServerQuestion({
                ...editorQuestion,
                prompt: event.target.value,
              }),
              false,
            )
          }
          placeHolder="Type your question..."
          maxLength={250}
        />
      )}
      {editorQuestion.choices &&
        onModifyQuestionChoice &&
        onDeleteQuestionChoice && (
          <MultipleChoiceOptionEditor
            question={editorQuestion}
            choices={editorQuestion.choices}
            onModifyQuestionChoice={onModifyQuestionChoice}
            onDeleteQuestionChoice={onDeleteQuestionChoice}
            onReorderQuestionChoices={handleReorderQuestionChoices}
            allowImages={true}
          />
        )}
      <div
        key={`question-${editorQuestion.index}-buttons`}
        className="action-buttons"
      >
        <ActionButton
          type="go"
          icon={faPlusSquare}
          onClick={() => onAddQuestionChoice(editorQuestion)}
          label="Add choice"
        />
        <ActionButton
          type="edit"
          icon={faPencil}
          onClick={() => onUpdateQuestion(editorQuestion, true)}
          label="Update"
        />
        {onDeleteQuestion && (
          <ActionButton
            type="delete"
            icon={faTrash}
            onClick={() => onDeleteQuestion(editorQuestion)}
            label="Delete"
          />
        )}
      </div>
    </>
  );
};

export default AssignmentQuestion;
